import React, { useMemo } from "react";
import { useTheme, Theme } from '../contexts/ThemeContext';

// RN-ALIGNED: Clean createStyles function matching React Native pattern
const createStyles = (theme: Theme) => ({
  container: {
    maxWidth: '672px',
    marginLeft: 'auto',
    marginRight: 'auto',
    padding: theme.spacing.xl,
    color: theme.colors.text,
  },
  title: {
    fontSize: theme.typography.sizes.xxxl,
    fontWeight: theme.typography.weights.bold,
    marginBottom: theme.spacing.lg,
    color: theme.colors.text,
  },
  sectionTitle: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: theme.typography.weights.semiBold,
    marginTop: theme.spacing.xl,
    marginBottom: theme.spacing.sm,
    color: theme.colors.text,
  },
  paragraph: {
    marginBottom: theme.spacing.sm,
    lineHeight: theme.typography.lineHeights.relaxed,
    color: theme.colors.text,
  },
  list: {
    marginLeft: theme.spacing.xl,
    marginBottom: theme.spacing.sm,
    listStyleType: 'decimal',
    color: theme.colors.text,
  },
  listItem: {
    marginBottom: theme.spacing.xs,
    lineHeight: theme.typography.lineHeights.relaxed,
  },
  link: {
    color: theme.colors.primary,
    textDecoration: 'underline',
  },
  separator: {
    marginTop: theme.spacing.xl,
    marginBottom: theme.spacing.xl,
    borderTopWidth: 1,
    borderTopStyle: 'solid' as const,
    borderTopColor: theme.colors.border,
  },
  contactParagraph: {
    marginBottom: theme.spacing.xl,
    color: theme.colors.text,
  },
});

const DeleteAccount: React.FC = () => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Delete Your Account</h1>
      <p style={styles.paragraph}>
        ChordBaker does not create traditional accounts with usernames and passwords.
        Instead, you can optionally connect two services — Google Drive (for sync on Android)
        and Buffet (for sharing chord charts publicly). You can disconnect either at any time
        directly from the app.
      </p>

      <h2 style={styles.sectionTitle}>Google Drive Sync (Android)</h2>
      <p style={styles.paragraph}>
        To disconnect your Google account and stop syncing chord charts with Google Drive:
      </p>
      <ol style={styles.list}>
        <li style={styles.listItem}>Open ChordBaker on your Android device</li>
        <li style={styles.listItem}>Tap the settings icon in the top right</li>
        <li style={styles.listItem}>Tap <strong>Google Drive Sync</strong></li>
        <li style={styles.listItem}>Tap <strong>Sign Out</strong> and confirm</li>
      </ol>
      <p style={styles.paragraph}>
        This immediately revokes ChordBaker's access to your Google Drive. Your chord charts
        remain on your device — only the sync connection is removed.
      </p>

      <h2 style={styles.sectionTitle}>Buffet Account (Chart Sharing)</h2>
      <p style={styles.paragraph}>
        If you signed in to Buffet to share chord charts publicly, you can disconnect your account:
      </p>
      <ol style={styles.list}>
        <li style={styles.listItem}>Open ChordBaker</li>
        <li style={styles.listItem}>Tap the settings icon in the top right</li>
        <li style={styles.listItem}>Tap <strong>Buffet</strong>, then tap <strong>Sign Out</strong></li>
      </ol>

      <h2 style={styles.sectionTitle}>Request Full Data Deletion</h2>
      <p style={styles.paragraph}>
        To permanently delete all data associated with your account — including any chord charts
        stored in Google Drive's app folder and your Buffet profile and uploaded charts — email us at:
      </p>
      <p style={styles.contactParagraph}>
        <a href="mailto:tomas@chordbaker.com" style={styles.link}>
          tomas@chordbaker.com
        </a>
      </p>
      <p style={styles.paragraph}>
        We will process your request within 30 days.
      </p>

      <div style={styles.separator} />

      <p style={styles.paragraph}>
        If you have any questions, contact us at{' '}
        <a href="mailto:tomas@chordbaker.com" style={styles.link}>
          tomas@chordbaker.com
        </a>
        .
      </p>
    </div>
  );
};

export default DeleteAccount;
